import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { AmazonLogo, GoodreadsLogo, Stars } from "@/components/site/brands";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { motion } from "framer-motion";

export default function BookDetail() {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [config, setConfig] = useState({});
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/books/${slug}`).then((r) => {
      setBook(r.data);
      document.title = r.data.meta_title || `${r.data.title} — Kate Mercer`;
    }).catch(() => setNotFound(true));
    api.get("/config").then((r) => setConfig(r.data || {})).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="App">
        <Navbar />
        <div className="min-h-[60vh] grid place-items-center px-6 text-center">
          <div>
            <h1 className="font-display text-4xl font-bold">Book not found</h1>
            <Link to="/" className="btn-gold rounded-full px-6 py-3 mt-6 inline-block text-sm font-semibold">Back home</Link>
          </div>
        </div>
        <Footer config={config} />
      </div>
    );
  }

  if (!book) return <div className="min-h-screen grid place-items-center">Loading…</div>;

  return (
    <div className="App bg-white">
      <Navbar />
      <section className="pt-28 sm:pt-36 pb-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Link to="/#books" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black mb-10" data-testid="book-back">
            <ArrowLeft size={16} /> Back to all books
          </Link>
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-start">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                        className="relative flex justify-center">
              <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-gold/25 to-transparent blur-2xl" />
              {book.cover_image
                ? <img src={resolveImg(book.cover_image)} alt={book.title} className="relative book-shadow rounded-xl w-[280px] sm:w-[340px] h-auto" />
                : <div className="relative w-[280px] aspect-[2/3] rounded-xl bg-gray-900 grid place-items-center text-gold">Coming Soon</div>}
            </motion.div>

            <div>
              <span className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">{book.category}</span>
              <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-3">{book.title}</h1>
              {book.subtitle && <p className="text-lg text-gray-500 mt-2">{book.subtitle}</p>}
              <div className="flex items-center gap-3 mt-4">
                <Stars rating={book.goodreads_rating || 5} />
                {book.goodreads_rating > 0 && (
                  <span className="text-sm text-gray-500">
                    {book.goodreads_rating.toFixed(1)}{book.goodreads_ratings_count > 0 && ` · ${book.goodreads_ratings_count} ratings on Goodreads`}
                  </span>
                )}
              </div>

              <p className="text-gray-700 leading-relaxed mt-7 text-base">{book.long_description || book.description}</p>

              <div className="grid sm:grid-cols-2 gap-3 mt-8 max-w-md">
                {["Practical, research-based advice", "Written for real students & graduates", "Immediately applicable systems", "Clear over hype, always"].map((f) => (
                  <div key={f} className="flex items-center gap-2.5 text-sm text-gray-700">
                    <span className="h-5 w-5 rounded-full bg-gold/15 grid place-items-center"><Check size={12} className="text-gold" strokeWidth={3} /></span>
                    {f}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 mt-9">
                {book.amazon_link && (
                  <a href={book.amazon_link} target="_blank" rel="noopener noreferrer" data-testid="detail-buy"
                     className="btn-gold btn-pill">
                    Buy on <AmazonLogo className="h-4 w-auto" /> <ArrowRight size={16} />
                  </a>
                )}
                {book.goodreads_link && (
                  <a href={book.goodreads_link} target="_blank" rel="noopener noreferrer" data-testid="detail-goodreads"
                     className="btn-pill bg-[#F4F1EA] hover:bg-[#eae4d6] transition-colors">
                    Reviews on <GoodreadsLogo className="h-4 w-auto" />
                  </a>
                )}
              </div>

              {(book.isbn || book.publication_date) && (
                <div className="mt-8 pt-6 border-t border-gray-100 flex flex-wrap gap-8 text-sm text-gray-500">
                  {book.publication_date && <div><span className="block text-xs uppercase tracking-wider text-gray-400">Published</span>{book.publication_date}</div>}
                  {book.isbn && <div><span className="block text-xs uppercase tracking-wider text-gray-400">ISBN</span>{book.isbn}</div>}
                  {book.category && <div><span className="block text-xs uppercase tracking-wider text-gray-400">Category</span>{book.category}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer config={config} />
    </div>
  );
}
